import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, TrendingDown, Sparkles, Send, Building2, Users, ChevronDown, Share2, Mail, FileText, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Toast } from '../Toast';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { Recommendation } from '../../data/mockRecommendationsData';
interface RecommendationDetailModalProps {
  recommendation: Recommendation | null;
  onClose: () => void;
}
interface Message {
  id: string;
  role: 'user' | 'assistant';
  text: string;
}
export function RecommendationDetailModal({
  recommendation,
  onClose
}: RecommendationDetailModalProps) {
  useEscapeKey(onClose, !!recommendation);
  const [query, setQuery] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showShareMenu, setShowShareMenu] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const shareMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (recommendation && messages.length === 0) {
      // Initial AI greeting
      setMessages([{
        id: '1',
        role: 'assistant',
        text: `I've identified that ${recommendation.rationale.toLowerCase()} This recommendation is expected to reduce risk by ${recommendation.riskReduction}%. What would you like to explore?`
      }]);
    }
  }, [recommendation]);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [messages, isTyping]);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(event.target as Node)) {
        setShowShareMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  if (!recommendation) return null;
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'high':
        return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'low':
        return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };
  const quickPrompts = ['Why is this recommended?', 'What is the cost of inaction?', 'Draft an implementation plan', 'Who should own this?', 'Show me the source data'];
  const generateMockResponse = (userQuery: string): string => {
    const lowerQuery = userQuery.toLowerCase();
    if (lowerQuery.includes('source') || lowerQuery.includes('data') || lowerQuery.includes('evidence')) {
      const driverList = recommendation.linkedDrivers.map(d => `• **${d}**`).join('\n');
      return `This recommendation is based on the following source data:\n\n**Linked Risk Drivers:**\n${driverList}\n\n**Key observations:** ${recommendation.activityExamples?.length || 0} affected activities identified across ${recommendation.projectsAffected || recommendation.contractorsAffected || 1} ${recommendation.projectsAffected ? 'projects' : 'contractors'}.\n\n**Estimated risk reduction:** ${recommendation.riskReduction}% based on historical intervention effectiveness.\n\nWould you like me to drill into a specific driver or show the underlying observations?`;
    }
    if (lowerQuery.includes('why') || lowerQuery.includes('recommended')) {
      return `This recommendation targets ${recommendation.linkedDrivers.join(', ')}. ${recommendation.rationale} Based on historical data, addressing these drivers has consistently reduced incident rates.`;
    }
    if (lowerQuery.includes('cost') || lowerQuery.includes('inaction')) {
      return `Continuing without this intervention could maintain the current ${recommendation.riskReduction}% elevated risk exposure. Based on similar patterns across the portfolio, this translates to increased likelihood of incidents and potential project delays.`;
    }
    if (lowerQuery.includes('draft') || lowerQuery.includes('plan')) {
      return `Here is a draft implementation plan:\n\n**Phase 1 (Next 48h):**\n- Issue safety bulletin to affected ${recommendation.scope === 'project' ? 'project team' : 'teams'}\n- Conduct supervisor briefing on ${recommendation.linkedDrivers[0]}\n\n**Phase 2 (Next Week):**\n- Implement ${recommendation.title.toLowerCase()}\n- Monitor metrics for improvement`;
    }
    if (lowerQuery.includes('who') || lowerQuery.includes('own')) {
      return `Based on the scope (${recommendation.scope}) and affected ${recommendation.projectsAffected ? `${recommendation.projectsAffected} projects` : `${recommendation.contractorsAffected} contractors`}, this should be owned by ${recommendation.scope === 'systemic' ? 'Corporate Safety Leader' : recommendation.scope === 'region' ? 'Regional Safety Director' : 'Project Manager'} with support from field supervision.`;
    }
    return `Implementing "${recommendation.title}" is estimated to reduce risk by ${recommendation.riskReduction}% across the affected ${recommendation.scope === 'project' ? 'project' : 'projects'}. The primary drivers are ${recommendation.linkedDrivers.join(', ')}.`;
  };
  const handleSend = (text: string) => {
    if (!text.trim()) return;
    const newUserMsg: Message = {
      id: Date.now().toString(),
      role: 'user',
      text
    };
    setMessages(prev => [...prev, newUserMsg]);
    setQuery('');
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      const newAiMsg: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        text: generateMockResponse(text)
      };
      setMessages(prev => [...prev, newAiMsg]);
    }, 1500);
  };
  const handleActionClick = (action: string) => {
    setToastMessage(`${action} initiated`);
    setShowToast(true);
    setShowShareMenu(false);
  };
  if (!recommendation) return null;
  return <AnimatePresence data-id="element-1981">
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6" data-id="element-1982">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" data-id="element-1983" />

        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-1984">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50" data-id="element-1985">
            <div className="flex items-center gap-3" data-id="element-1986">
              <div className="p-2 bg-purple-100 rounded-lg" data-id="element-1987">
                <Sparkles size={20} className="text-purple-600" data-id="element-1988" />
              </div>
              <div data-id="element-1989">
                <h2 className="text-lg font-bold text-gray-900" data-id="element-1990">
                  AI Recommendation
                </h2>
                <p className="text-sm text-gray-500" data-id="element-1991">
                  {recommendation.scope === 'systemic' ? 'Enterprise-wide intervention' : recommendation.scope === 'region' ? 'Regional intervention' : 'Project-specific action'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2" data-id="element-1992">
              <div className="relative" ref={shareMenuRef} data-id="element-1993">
                <button onClick={() => setShowShareMenu(!showShareMenu)} className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-1994">
                  <Share2 size={16} data-id="element-1995" />
                  Take Action
                  <ChevronDown size={14} data-id="element-1996" />
                </button>

                <AnimatePresence data-id="element-1997">
                  {showShareMenu && <motion.div initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: 10
                }} className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-10" data-id="element-1998">
                      <button onClick={() => handleActionClick('Drafting email')} className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 text-left" data-id="element-1999">
                        <Mail size={16} className="text-gray-400" data-id="element-2000" />
                        Draft email to team
                      </button>
                      <button onClick={() => handleActionClick('Creating action item')} className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 text-left" data-id="element-2001">
                        <CheckCircle2 size={16} className="text-gray-400" data-id="element-2002" />
                        Create action item
                      </button>
                      <button onClick={() => handleActionClick('Generating report')} className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 text-left" data-id="element-2003">
                        <FileText size={16} className="text-gray-400" data-id="element-2004" />
                        Generate implementation report
                      </button>
                    </motion.div>}
                </AnimatePresence>
              </div>
              <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-lg transition-colors" data-id="element-2005">
                <X size={20} data-id="element-2006" />
              </button>
            </div>
          </div>

          <div className="flex flex-col md:flex-row flex-1 overflow-hidden" data-id="element-2007">
            {/* Left Column: Recommendation Details */}
            <div className="w-full md:w-1/2 border-r border-gray-200 overflow-y-auto p-6 bg-white" data-id="element-2008">
              <div className="mb-6" data-id="element-2009">
                <div className="flex items-center gap-2 mb-3" data-id="element-2010">
                  <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold rounded-full border uppercase tracking-wider ${getPriorityColor(recommendation.priority)}`} data-id="element-2011">
                    {recommendation.priority === 'critical' && <AlertTriangle size={10} data-id="element-2012" />}
                    {recommendation.priority}
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-purple-50 border border-purple-200 text-[10px] font-semibold text-purple-600" data-id="element-2013">
                    <Sparkles size={10} data-id="element-2014" />
                    AI
                  </span>
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-2 leading-tight" data-id="element-2015">
                  {recommendation.title}
                </h3>

                <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-2016">
                  <div className="p-4 rounded-xl border bg-emerald-50 border-emerald-100" data-id="element-2017">
                    <p className="text-xs font-semibold mb-1 uppercase tracking-wider text-emerald-800" data-id="element-2018">
                      Risk Reduction
                    </p>
                    <div className="flex items-baseline gap-1" data-id="element-2019">
                      <span className="text-3xl font-bold text-emerald-600" data-id="element-2020">
                        -{recommendation.riskReduction}%
                      </span>
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl" data-id="element-2021">
                    <p className="text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider" data-id="element-2022">
                      Scope
                    </p>
                    <div className="flex items-center gap-2" data-id="element-2023">
                      <span className="text-lg font-bold text-gray-900 capitalize" data-id="element-2024">
                        {recommendation.scope}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mb-6" data-id="element-2025">
                  <h4 className="text-sm font-bold text-gray-900 mb-2" data-id="element-2026">
                    Rationale
                  </h4>
                  <p className="text-sm text-gray-600 leading-relaxed" data-id="element-2027">
                    {recommendation.rationale}
                  </p>
                </div>

                <div className="flex items-center gap-6 mb-6 py-4 border-y border-gray-100" data-id="element-2028">
                  {recommendation.projectsAffected && <div className="flex items-center gap-2" data-id="element-2029">
                      <Building2 size={18} className="text-gray-400" data-id="element-2030" />
                      <span className="text-sm font-medium text-gray-700" data-id="element-2031">
                        {recommendation.projectsAffected} Projects
                      </span>
                    </div>}
                  {recommendation.contractorsAffected && <div className="flex items-center gap-2" data-id="element-2032">
                      <Users size={18} className="text-gray-400" data-id="element-2033" />
                      <span className="text-sm font-medium text-gray-700" data-id="element-2034">
                        {recommendation.contractorsAffected} Contractors
                      </span>
                    </div>}
                </div>

                <div className="mb-6" data-id="element-2035">
                  <h4 className="text-sm font-bold text-gray-900 mb-3" data-id="element-2036">
                    Expected Impact
                  </h4>
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl" data-id="element-2037">
                    <p className="text-sm text-amber-900 leading-relaxed" data-id="element-2038">
                      {recommendation.expectedImpact}
                    </p>
                  </div>
                </div>

                <div className="mb-6" data-id="element-2039">
                  <h4 className="text-sm font-bold text-gray-900 mb-3" data-id="element-2040">
                    Linked Risk Drivers
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-2041">
                    {recommendation.linkedDrivers.map(driver => <span key={driver} className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-100 px-2.5 py-1 rounded-lg" data-id="element-2042">
                        {driver}
                      </span>)}
                  </div>
                </div>

                {recommendation.activityExamples && recommendation.activityExamples.length > 0 && <div data-id="element-2043">
                      <h4 className="text-sm font-bold text-gray-900 mb-3" data-id="element-2044">
                        Affected Activities
                      </h4>
                      <div className="space-y-2" data-id="element-2045">
                        {recommendation.activityExamples.map((ex, idx) => <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-3" data-id="element-2046">
                            <div className="flex items-center gap-2 mb-1" data-id="element-2047">
                              <span className="text-sm font-bold text-gray-900" data-id="element-2048">
                                {ex.activityName}
                              </span>
                              {recommendation.scope !== 'project' && <span className="text-[10px] font-medium text-gray-500 bg-white px-1.5 py-0.5 rounded border border-gray-200" data-id="element-2049">
                                  {ex.projectName}
                                </span>}
                            </div>
                            <p className="text-xs text-gray-600" data-id="element-2050">
                              {ex.explanation}
                            </p>
                          </div>)}
                      </div>
                    </div>}
              </div>
            </div>

            {/* Right Column: AI Chat */}
            <div className="w-full md:w-1/2 flex flex-col bg-gray-50" data-id="element-2051">
              <div className="p-4 border-b border-gray-200 bg-white shadow-sm z-10" data-id="element-2052">
                <div className="flex items-center gap-2" data-id="element-2053">
                  <Sparkles size={18} className="text-purple-600" data-id="element-2054" />
                  <h3 className="font-bold text-gray-900" data-id="element-2055">
                    Discuss with Bilton AI
                  </h3>
                </div>
                <p className="text-xs text-gray-500 mt-1" data-id="element-2056">
                  Ask questions to explore this recommendation in depth.
                </p>
              </div>

              {/* Chat Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4" data-id="element-2057">
                {messages.map(msg => <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-2058">
                    <div className={`max-w-[85%] p-3 rounded-2xl text-sm ${msg.role === 'user' ? 'bg-purple-600 text-white rounded-tr-sm' : 'bg-white border border-gray-200 text-gray-800 rounded-tl-sm shadow-sm'}`} data-id="element-2059">
                      {msg.role === 'assistant' && <div className="flex items-center gap-1.5 mb-1.5 text-purple-600" data-id="element-2060">
                          <Sparkles size={12} data-id="element-2061" />
                          <span className="text-xs font-bold uppercase tracking-wider" data-id="element-2062">
                            Bilton AI
                          </span>
                        </div>}
                      <div className="whitespace-pre-wrap leading-relaxed" data-id="element-2063">
                        {msg.text}
                      </div>
                    </div>
                  </div>)}

                {isTyping && <div className="flex justify-start" data-id="element-2064">
                    <div className="bg-white border border-gray-200 p-4 rounded-2xl rounded-tl-sm shadow-sm flex gap-1" data-id="element-2065">
                      <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" data-id="element-2066" />
                      <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{
                    animationDelay: '0.2s'
                  }} data-id="element-2067" />
                      <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{
                    animationDelay: '0.4s'
                  }} data-id="element-2068" />
                    </div>
                  </div>}
                <div ref={messagesEndRef} data-id="element-2069" />
              </div>

              {/* Chat Input */}
              <div className="p-4 bg-white border-t border-gray-200" data-id="element-2070">
                {/* Quick Prompts */}
                <div className="flex flex-wrap gap-2 mb-3" data-id="element-2071">
                  {quickPrompts.map((prompt, idx) => <button key={idx} onClick={() => handleSend(prompt)} className="px-3 py-1.5 bg-purple-50 hover:bg-purple-100 text-purple-700 text-xs font-medium rounded-full transition-colors whitespace-nowrap" data-id="element-2072">
                      {prompt}
                    </button>)}
                </div>

                <div className="relative" data-id="element-2073">
                  <input type="text" value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSend(query)} placeholder="Ask about this recommendation..." className="w-full pl-4 pr-12 py-3 bg-gray-50 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-shadow" data-id="element-2074" />
                  <button onClick={() => handleSend(query)} disabled={!query.trim() || isTyping} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:hover:bg-purple-600 transition-colors" data-id="element-2075">
                    <Send size={16} data-id="element-2076" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <Toast message={toastMessage} isVisible={showToast} onClose={() => setShowToast(false)} data-id="element-2077" />
    </AnimatePresence>;
}